import { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { FolderPlusIcon } from 'lucide-react';
import { Accept, useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { focusVisibleRing } from '@/components/ui/constants';
import useFileUpload from '@/hooks/useFileUpload';
import { cn } from '@/lib/utils';
import { UploadedFile } from '@/shared/types/server';
import { FIFTEEN_MB, HUNDRED_BYTES } from '@/util/constants';
import { safelyGetErrorMessage } from '@/util/errors';

import useFileSubmit from './useFileSubmit';
import { processFiles, handleErrors, AcceptedType } from './utils';

type BaseProps = {
  message: React.ReactNode;
  messageImage?: boolean;
  maxSizeInBytes?: number;
  minSizeInBytes?: number;
  accept?: Accept;
  stream?: boolean;
  uploadPath?: string;
  parseJson?: boolean;
  onError?: (error: string) => void;
  noGutters?: boolean;
  restrictedNames?: string[];
  disabled?: boolean;
  isLoadingProp?: boolean;
  forceError?: string;
  // Render only the dropzone UI
  dropzoneOnly?: boolean;
  // Upload/parse error callback
  onUploadError?: (message: string) => void;
  onUploadStart?: () => void;
};

type MultipleUploadProps = {
  multiple: true;
  setUrls: Dispatch<SetStateAction<{ url: string; name: string; type?: string }[]>>;
  setFilesLength: Dispatch<SetStateAction<number>>;
  files: AcceptedType[];
  setFiles: Dispatch<SetStateAction<AcceptedType[]>>;
  duplicates: AcceptedType[];
  setDuplicates: Dispatch<SetStateAction<AcceptedType[]>>;
  onReplaceAll: (x: (x: AcceptedType[]) => Promise<void>) => void;
  onMergeAll: (x: (x: AcceptedType[]) => Promise<void>) => void;
  duplicatesLength: number;
};

type SingleUploadProps = {
  fileName?: string;
  onFileUploaded?: (f: UploadedFile) => void;
  shouldCrop?: boolean;
  aspectRatio?: 'horizontal' | 'square';
  onFileRemoved?: () => void;
};

type Props = BaseProps & (MultipleUploadProps | SingleUploadProps);

const DROPZONE_CLASS = cn(
  'flex h-full min-h-[65px] flex-col items-center justify-center rounded bg-background text-base leading-none font-normal text-informational group-hover:bg-highlight-background group-active:bg-highlight-background',
  focusVisibleRing
);

const FILE_SELECTED_CLASS = 'flex w-full items-center justify-between';

// A disabled zone with an error drops the red hover and falls back to the default one
const getSectionClass = (hasError: boolean, disabled: boolean) =>
  cn(
    'group relative h-full cursor-pointer rounded border border-dashed text-center',
    hasError ? 'border-stroke-destructive' : 'border-stroke',
    hasError && !disabled ? 'hover:bg-destructive' : 'hover:bg-highlight-background',
    hasError && disabled && 'hover:border-stroke'
  );

const FileUpload = (props: Props) => {
  const {
    multiple,
    setUrls,
    setFilesLength,
    onReplaceAll,
    onMergeAll,
    setFiles,
    setDuplicates,
    duplicatesLength,
  } = props as MultipleUploadProps;

  const {
    fileName,
    onFileUploaded,
    shouldCrop = false,
    aspectRatio,
    onFileRemoved,
  } = props as SingleUploadProps;

  const {
    message,
    maxSizeInBytes = FIFTEEN_MB,
    minSizeInBytes = HUNDRED_BYTES,
    accept,
    stream,
    uploadPath,
    parseJson,
    onError,
    messageImage,
    noGutters = false,
    restrictedNames = [],
    disabled = false,
    isLoadingProp,
    forceError,
    dropzoneOnly = false,
    onUploadError,
    onUploadStart,
  } = props;

  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = useState<string>(fileName);
  const [error, setError] = useState(forceError || null);
  const { croppedFile, onAvatarUpload } = useFileUpload(aspectRatio);
  const [isLoading, setIsLoading] = useState(false);

  const {
    uploadFile,
    data,
    error: uploadFileError,
    isLoading: loading,
  } = useFileSubmit({ stream, uploadPath, parseJson });
  const isUploading = isLoading || loading || isLoadingProp;
  useEffect(() => {
    if (croppedFile) {
      uploadFile(croppedFile);
    }
  }, [croppedFile, uploadFile]);

  useEffect(() => {
    setSelectedFile(fileName);
  }, [fileName]);

  useEffect(() => {
    if (data) {
      if (multiple) {
        setUrls((prev) => [...prev, { url: data.url, name: data.name, type: data?.type }]);
      }
      if (onFileUploaded) {
        onFileUploaded(data);
      }
    }
  }, [data, multiple, onFileUploaded, setUrls]);

  useEffect(() => {
    if (uploadFileError) {
      // In dropzoneOnly mode this internal error is not rendered
      // The parent handles error display via onUploadError
      setError(uploadFileError.message);
      onUploadError?.(uploadFileError.message);
    }
  }, [uploadFileError, onUploadError]);

  const handleOnRemoveFile = async (e: React.MouseEvent<HTMLButtonElement>) => {
    // Prevent file dialog box to open
    e.stopPropagation();

    setSelectedFile(null);
    if (onFileRemoved) {
      onFileRemoved();
    }
  };

  const handleClearError = () => {
    if (setFiles) {
      setFiles([]);
    }
    if (setDuplicates) {
      setDuplicates([]);
    }
    if (onError) {
      onError(null);
    }
    setSelectedFile(null);
    setError(null);
    if (onFileRemoved) {
      onFileRemoved();
    }
  };

  const processAndFilterFiles = async (acceptedFiles) => {
    try {
      const { nonRestrictedFiles } = await processFiles(acceptedFiles, restrictedNames);
      return nonRestrictedFiles;
    } catch (error) {
      if (setFiles) {
        setFiles(error.nonRestrictedFiles || []);
      }

      if (setDuplicates && error.duplicateFiles) {
        setDuplicates(error.duplicateFiles || []);
        toast.info(
          t('file_upload.x_files_exist', {
            0: error.duplicateFiles.length,
            count: error.duplicateFiles.length,
          })
        );
      }

      setError(error.message);

      return null;
    }
  };

  const uploadFiles = async (files) => {
    try {
      if (!multiple) {
        const file = files[0];
        if (shouldCrop) {
          onAvatarUpload(undefined, file, { primaryButtonText: t('file_upload.upload') });
        } else {
          uploadFile(file);
        }
        setSelectedFile(file.path);
      } else {
        setFilesLength(files.length);
        setIsLoading(true);
        // `forEach`, not `for...of`: the React Compiler skips a component with a loop inside `try`
        files.forEach((file) => uploadFile(file));
        setIsLoading(false);
      }
    } catch (error) {
      setError(error.message);
      setIsLoading(false);
      if (onError) {
        onError(error);
      }
      if (setFiles) {
        setFiles([]);
      }
      if (setDuplicates) {
        setDuplicates([]);
      }
      console.error(error);
    }
  };

  const processFilesForUpload = async (files) => {
    const nonRestrictedFiles = await processAndFilterFiles(files);
    // if !nonRestrictedFiles, it means that the `processFiles` promise was rejected
    if (nonRestrictedFiles) {
      await uploadFiles(nonRestrictedFiles);
    }
  };

  const onDrop = async (acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length > 0) {
      handleErrors(rejectedFiles, minSizeInBytes, maxSizeInBytes, accept);
      return;
    }
    onUploadStart?.();
    await processFilesForUpload(acceptedFiles);
  };

  const handleMergeAll = async () => {
    onMergeAll(uploadFiles);
    setError(null);
  };

  const handleReplaceAll = async () => {
    onReplaceAll(uploadFiles);
    setError(null);
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept,
    maxSize: maxSizeInBytes,
    minSize: minSizeInBytes,
    disabled: isUploading || disabled,
    multiple,
    noDragEventsBubbling: true,
    onDrop,
  });

  const fileSelectedTextClass = noGutters ? 'max-w-20 overflow-hidden p-0 text-ellipsis' : 'p-4';

  const renderError = () => (
    <div className={FILE_SELECTED_CLASS}>
      <p className={cn('text-left text-destructive', fileSelectedTextClass)}>
        {t('file_upload.error', { 0: safelyGetErrorMessage(error) })}
      </p>
      <p className={fileSelectedTextClass}>
        <Button variant="destructive" size="sm" onClick={handleClearError}>
          {t('common.clear')}
        </Button>
      </p>
    </div>
  );

  const renderDuplicateError = () => (
    <div>
      <p className="p-4">
        {t('file_upload.x_files_exist', { 0: duplicatesLength, count: duplicatesLength })}
      </p>

      <p className="p-4 text-foreground-muted">
        {t('file_upload.replace_or_merge', { 0: duplicatesLength, count: duplicatesLength })}
      </p>
      <p className="p-4">
        <Button variant="ghost-neutral" size="sm" onClick={handleClearError}>
          {t('common.clear')}
        </Button>
        <Button variant="outline-neutral" size="sm" onClick={handleMergeAll}>
          {t('file_upload.merge_all', { 0: duplicatesLength, count: duplicatesLength })}
        </Button>
        <Button size="sm" onClick={handleReplaceAll}>
          {t('file_upload.replace_all', { 0: duplicatesLength, count: duplicatesLength })}
        </Button>
      </p>
    </div>
  );

  const renderSelectedFile = () => (
    <div className={FILE_SELECTED_CLASS}>
      <p className={fileSelectedTextClass}>{selectedFile}</p>

      <Button
        className="mr-4"
        isLoading={isUploading}
        variant="outline-neutral"
        size="sm"
        onClick={handleOnRemoveFile}
        disabled={disabled}
      >
        {t('common.remove')}
      </Button>
    </div>
  );

  const renderDropdown = () => (
    <>
      <input {...getInputProps()} />
      {messageImage && <FolderPlusIcon className="text-foreground-muted" size={57} />}
      <p className="p-4">{message}</p>
    </>
  );

  // `dropzoneOnly` keeps the zone at a constant size in every state; the parent
  // renders the selected-file chip and any error/validation messages below it.
  if (dropzoneOnly) {
    return (
      <section className={getSectionClass(false, disabled)}>
        <div {...getRootProps({ className: DROPZONE_CLASS })}>{renderDropdown()}</div>
      </section>
    );
  }

  const hasError = error && error !== 'duplicates';
  return (
    <section className={getSectionClass(!!hasError, disabled)}>
      <div {...getRootProps({ className: DROPZONE_CLASS })}>
        {hasError && renderError()}
        {error === 'duplicates' && renderDuplicateError()}
        {!error && selectedFile && renderSelectedFile()}
        {!error && !selectedFile && renderDropdown()}
      </div>
    </section>
  );
};

export default FileUpload;
